import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Cloud as CloudIcon,
  Satellite as SatelliteIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const NoSatelliteDataSlug = () => {
  const { t } = useTranslation();
  return (
    <Card
      sx={{
        my: 3,
        mx: 'auto',
        maxWidth: 600,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        border: '1px solid #dee2e6',
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <SatelliteIcon
            sx={{
              fontSize: 48,
              color: '#6c757d',
              mr: 2,
            }}
          />
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#495057',
                mb: 0.5,
              }}
            >
              {t('No Recent Satellite Data')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Data from the last 7 days is unavailable')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Possible Reasons */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: '#495057',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <InfoIcon sx={{ mr: 1, color: '#17a2b8' }} />
            {t('Possible Reasons')}
          </Typography>

          <List sx={{ py: 0 }}>
            <ListItem
              sx={{
                py: 1.5,
                px: 2,
                mb: 1,
                bgcolor: 'rgba(108, 117, 125, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(108, 117, 125, 0.2)',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CloudIcon sx={{ color: '#6c757d', fontSize: 28 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {t('Cloud Cover')}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'Heavy cloud coverage prevents clear satellite imagery acquisition'
                    )}
                  </Typography>
                }
              />
            </ListItem>

            <ListItem
              sx={{
                py: 1.5,
                px: 2,
                bgcolor: 'rgba(108, 117, 125, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(108, 117, 125, 0.2)',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SatelliteIcon sx={{ color: '#6c757d', fontSize: 28 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {t('Satellite Coverage Gap')}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'This farm location was not covered by satellite passes in the recent period'
                    )}
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>

        {/* Info Alert */}
        <Alert
          icon={<ScheduleIcon />}
          severity="info"
          sx={{
            bgcolor: 'rgba(23, 162, 184, 0.1)',
            border: '1px solid rgba(23, 162, 184, 0.3)',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: '#17a2b8',
            },
          }}
        >
          <Typography variant="body2">
            <strong>{`${t("What's next")} ?`}</strong>{' '}
            {t(
              'Satellite data is typically updated every 2-3 days. Please check back soon for the latest imagery and analysis.'
            )}
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default NoSatelliteDataSlug;
